import { DateTime } from 'luxon';
import { z } from 'zod';

import { PLATE_STATE_OPTIONS } from '@/constants';
import { parseISO8601DateTime } from '@/utils/dates';
import { PLATE_CHARS_MESSAGE, zodTimeStamp } from '@/utils/zodUtils';

import {
  HearingOfficerFeeSchema,
  ViolationPricingScheduleSchema,
} from './location';
import { createPaginatedResultSchema } from './shared/paginated';
import { ViolationStatusEnum } from './shared/violation-statuses';

export {
  VIOLATION_STATUS_LABELS,
  VIOLATION_STATUSES,
  type ViolationStatus,
  ViolationStatusEnum,
} from './shared/violation-statuses';

export const ViolationSourceEnum = z.enum(['peo', 'camera', 'unknown']);

export const Violation = z.object({
  locationId: z.string(),
  plate: z.string(),
  violationId: z.string(),
  createdAt: z.string().transform((val) => parseISO8601DateTime(val)),
  status: ViolationStatusEnum,
  currentAmountDue: z
    .object({
      value: z.number(),
      tax: z.number(),
    })
    .optional(), // TODO: make it required once dev API is up to date with prod
  imagePaths: z.array(z.string()).nullable().optional(),
  cameraEventIds: z.array(z.string()).nullable(),
  notes: z.string().optional(),
  externalNotes: z.string().max(500).nullish(),
  plateState: z.string().optional(),
  vehicleMake: z.string().optional(),
  vehicleModel: z.string().optional(),
  vehicleColor: z.string().optional(),
  reason: z.string().optional(),
  citationDueDate: z.string().optional(),
  invoiceId: z.string().optional(),
  /** True when linked enforcement invoice is under a Stripe dispute. */
  has_dispute: z.boolean().optional(),
  parkpliantId: z.string().optional(),
  amountPaid: z.number().optional(),
  discountValidDays: z.number().optional(),
  statusHistory: z
    .array(
      z.object({
        previousStatus: z.string(),
        newStatus: z.string(),
        updatedAt: z.string().transform((val) => parseISO8601DateTime(val)),
        updatedBy: z.string(),
      })
    )
    .nullable(),
  sessions: z.object({
    reservationId: z.string().optional(),
    permitId: z.string().optional(),
    validationId: z.string().optional(),
  }),
  pricingSchedule: ViolationPricingScheduleSchema,
  hearingOfficerFees: z.array(HearingOfficerFeeSchema).nullish(),
  systemReason: z.string().nullish(),
  arbitrationCaseId: z.string().nullish().optional(),
  waiveReason: z.string().nullish(),
  waiveReasonDetail: z.string().nullish(),
  voidReason: z.string().nullish(),
  voidReasonDetail: z.string().nullish(),
  // Officer fields (for public/government enforcement)
  officerId: z.string().nullish(),
  officerName: z.string().nullish(),
  officerBadgeNumber: z.string().nullish(),
  address: z.string().nullish(),
  blockFaceId: z.string().uuid().nullish(),
  source: ViolationSourceEnum.catch('unknown'),
  vcmsTicketNumber: z.string().nullish(),
  cityHearingAt: zodTimeStamp.nullish(),
  lastUndeliverableReason: z.string().max(255).nullish(),
  lastUndeliverableAt: zodTimeStamp.nullish(),
  workflow: z.string().nullish(),
});

export const COURTWARE_WORKFLOW = 'courtware';

/**
 * Body of `POST /stripe/pay`. `amount` is in cents. The API rejects
 * `checkNumber` unless the method is check and `paidAt` for courtware, and
 * treats an empty `paidAt` as an error, so absent values must be omitted.
 */
export type MarkViolationPaidPayload = {
  violationId: string;
  amount: number;
} & (
  | { method: 'check'; checkNumber: string; paidAt?: string }
  | { method: 'cash'; paidAt?: string }
  | { method: 'courtware' }
);

export const StatusHistory = z.object({
  previousStatus: z.string(),
  newStatus: z.string(),
  updatedAt: z.string().transform((val) => parseISO8601DateTime(val)),
  updatedBy: z.string(),
});

export const ViolationUpdateSchema = z
  .object({
    // Accepts all statuses for form pre-population (violations may have system-driven statuses).
    // The UI disables system-driven statuses in the dropdown; the backend binding tag enforces allowed transitions.
    status: ViolationStatusEnum,
    reason: z.string().optional(),
    plate: z.string().optional(),
    plateState: z.string(),
    vehicleMake: z.string().optional(),
    vehicleModel: z.string().optional(),
    vehicleColor: z.string().optional(),
    notes: z
      .string()
      .max(500, 'Internal notes cannot exceed 500 characters')
      .optional(),
    externalNotes: z.string().max(500).nullish(),
    discountValidDays: z.number().optional(),
    locationId: z.string(),
    arbitrationCaseId: z.string().nullish().optional(),
    waiveReason: z.string().optional(),
    waiveReasonDetail: z.string().max(500).optional(),
    voidReason: z.string().optional(),
    voidReasonDetail: z.string().max(500).optional(),
    cityHearingAt: z
      .custom<DateTime>(
        (val) => val == null || (DateTime.isDateTime(val) && val.isValid),
        'Must be a valid date and time'
      )
      .nullish(),
    // .nullish() matches the response shape (Violation returns null for
    // non-upheld violations). Using .optional() rejects null, which would
    // silently fail form submission whenever the form is initialized from a
    // refetched non-upheld violation via useViolationForm's values:initialData.
    hearingOfficerFees: z.array(HearingOfficerFeeSchema).nullish(),
  })
  .superRefine((val, ctx) => {
    if (val.status === 'confirmed' && !val.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reason is required if status is confirmed',
        path: ['reason'],
      });
    }
    if (val.status === 'waived' && !val.waiveReason?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Waive reason is required when status is waived',
        path: ['waiveReason'],
      });
    }
    if (
      val.status === 'waived' &&
      val.waiveReason === 'other' &&
      !val.waiveReasonDetail?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Describe the reason when "Other" is selected',
        path: ['waiveReasonDetail'],
      });
    }
    if (val.status === 'voided' && !val.voidReason?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Void reason is required when status is voided',
        path: ['voidReason'],
      });
    }
    if (
      val.status === 'voided' &&
      val.voidReason === 'other' &&
      !val.voidReasonDetail?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Describe the reason when "Other" is selected',
        path: ['voidReasonDetail'],
      });
    }
  });

export const CameraEvent = z.object({
  eventId: z.string(),
  plate: z.string(),
  plateState: z.string(),
  capturedAtUnix: z.number(),
  plateConfidence: z.number(),
  packetCounter: z.string(),
  geoTag: z.object({
    lat: z.number(),
    lon: z.number(),
  }),
  cameraInfo: z.object({
    serialNumber: z.string(),
    prodShortName: z.string(),
    macAddress: z.string(),
  }),
  imagePath: z.string().optional(),
  moveDirection: z.enum(['unknown', 'unk', 'in', 'out']),
  locationId: z.string(),
  croppedImagePath: z.string(),
  plateCoordinates: z.array(z.number()),
  plateCoordinatesRelative: z.array(z.number()),
  isConfirmed: z.boolean(),
});

export const CollectionEvent = z.object({
  id: z.string(),
  violation_id: z.string(),
  type: z.enum(['correction', 'dispute', 'notice', 'reassignment']),
  payload: z.any(),
  created_at: z.string().transform((val) => parseISO8601DateTime(val)),
});

export const CollectionNotice = z.object({
  referenceId: z.string(),
  type: z.enum(['letter', 'sms']),
  date: z.string().transform((val) => parseISO8601DateTime(val)),
  contentUrl: z.string().optional(),
  deliverTo: z.string().optional(),
});

export const CollectionCorrection = z.object({
  referenceId: z.string(),
  plate: z.string().optional(),
  state: z.string().optional(),
  make: z.string().optional(),
  void: z.boolean().optional(),
});

export const CollectionDispute = z.object({
  referenceId: z.string().optional(),
  uuid: z.string().optional(),
  createdUtc: z.string().transform((val) => parseISO8601DateTime(val)),
  privateUrl: z.string().optional(),
  text: z.string().optional(),
  customer: z
    .object({
      name: z.string(),
      email: z.string(),
    })
    .optional(),
  reason: z.enum([
    'Not_My_Car',
    'Already_Paid',
    'Waived',
    'Permit',
    'Machine_Broken',
    'Other',
  ]),
});

export const CustomerInfo = z.object({
  email: z.string(),
  name: z.string(),
});

export const Amount = z.object({
  value: z.number(),
  currency: z.string(),
});

export const PaymentMethod = z.object({
  type: z.string(),
  last4: z.string().optional(),
  cardBrand: z.string().optional(),
});

export const Tax = z.object({
  amount: z.number(),
  rate: z.number(),
});

export const Discount = z.object({
  amount: z.number(),
  tax: Tax,
  endTime: z.string().transform((val) => parseISO8601DateTime(val)),
  isValid: z.boolean(),
});

export const InvoiceItem = z.object({
  amount: z.number(),
  description: z.string().optional().default(''),
  currency: z.string().optional().default('usd'),
  discount: Discount.optional(),
  tax: Tax.optional(),
  metadata: z
    .object({
      violationId: z.string().optional().default(''),
      locationId: z.string().optional().default(''),
      isFee: z.boolean().optional().default(false),
      reason: z.string().optional().default(''),
      discountAppliedWhenPaid: z.boolean().optional(),
    })
    .optional(),
  itemMetadata: z.record(z.string(), z.string()).optional(),
  discountAppliedWhenPaid: z.boolean().optional().default(false),
});

const InvoiceVehicleInfoSchema = z.object({
  violationId: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  licensePlate: z.string().optional(),
  plateState: z.string().optional(),
  locationName: z.string().optional(),
});

export const Invoice = z.object({
  invoiceId: z.string(),
  invoiceItems: z.array(InvoiceItem).optional().default([]),
  paymentIntentClientSecret: z.string().optional().default(''),
  paymentIntentId: z.string().optional().default(''),
  total: Amount.optional(),
  customer: CustomerInfo.optional(),
  status: z.enum(['PAID', 'OPEN', 'REFUNDED', 'DISPUTED', 'CANCELED']),
  createdAt: z.string().transform((val) => parseISO8601DateTime(val)),
  paidAt: z
    .string()
    .transform((val) => parseISO8601DateTime(val))
    .optional(),
  paymentMethod: PaymentMethod.optional(),
  metaData: z.record(z.string()).optional().default({}),
  metadata: z.record(z.string()).optional(),
  refundId: z.string().optional(),
  refundStatus: z.string().optional(),
  refundCreatedAt: z
    .string()
    .transform((val) => parseISO8601DateTime(val))
    .optional(),
  disputeID: z.string().optional(),
  disputeAmount: Amount.optional(),
  disputeCreatedAt: z
    .string()
    .transform((val) => parseISO8601DateTime(val))
    .optional(),
  disputeStatus: z.string().optional(),
  disputeReason: z.string().optional(),
  disputeRespondDueBy: z
    .string()
    .transform((val) => parseISO8601DateTime(val))
    .optional(),
  vehicleInfo: InvoiceVehicleInfoSchema.optional(),
  vehicleInfoList: z.array(InvoiceVehicleInfoSchema).optional(),
  // TODO: remove in the future
  discountAppliedWhenPaid: z.boolean().optional(),
});

/** Loose list schema for disputes queue (GET /stripe/invoice).
 * Full Invoice schema is too strict for dump-all responses (discounts, zero times, etc.).
 */
export const DisputeInvoiceListItem = z
  .object({
    invoiceId: z.string(),
    status: z.string(),
    disputeID: z.string().optional().nullable(),
    disputeStatus: z.string().optional().nullable(),
    disputeReason: z.string().optional().nullable(),
    disputeAmount: Amount.partial({ currency: true })
      .or(z.number())
      .optional()
      .nullable(),
    disputeCreatedAt: z.union([z.string(), z.null()]).optional(),
    disputeRespondDueBy: z.union([z.string(), z.null()]).optional(),
    paymentIntentId: z.string().optional().nullable(),
    total: Amount.partial({ currency: true }).optional().nullable(),
    customer: z
      .object({
        email: z.string().optional().nullable(),
        name: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
    createdAt: z.union([z.string(), z.null()]).optional(),
    paidAt: z.union([z.string(), z.null()]).optional(),
    refundId: z.string().optional().nullable(),
    refundStatus: z.string().optional().nullable(),
    refundCreatedAt: z.union([z.string(), z.null()]).optional(),
    invoiceItems: z
      .array(
        z
          .object({
            metadata: z
              .object({
                locationId: z.string().optional().nullable(),
                violationId: z.string().optional().nullable(),
                isFee: z.boolean().optional().nullable(),
                reason: z.string().optional().nullable(),
              })
              .passthrough()
              .optional()
              .nullable(),
          })
          .passthrough()
      )
      .optional()
      .nullable(),
    vehicleInfo: InvoiceVehicleInfoSchema.optional().nullable(),
    vehicleInfoList: z.array(InvoiceVehicleInfoSchema).optional().nullable(),
  })
  .passthrough();
export type DisputeInvoiceListItem = z.infer<typeof DisputeInvoiceListItem>;
export const DisputeInvoiceList = z.array(DisputeInvoiceListItem);

export type ViolationType = z.infer<typeof Violation>;
export type ViolationSource = z.infer<typeof ViolationSourceEnum>;
export const ViolationList = z.array(Violation);
export type ViolationListType = z.infer<typeof ViolationList>;
export type ViolationUpdateSchemaType = z.infer<typeof ViolationUpdateSchema>;

export type StatusHistoryType = z.infer<typeof StatusHistory>;
export const StatusHistoryList = z.array(StatusHistory);
export type StatusHistoryListType = z.infer<typeof StatusHistoryList>;

export type CollectionEventType = z.infer<typeof CollectionEvent>;
export const CollectionEventList = z.array(CollectionEvent);
export type CollectionEventListType = z.infer<typeof CollectionEventList>;

export type CollectionNoticeType = z.infer<typeof CollectionNotice>;
export const CollectionNoticeList = z.array(CollectionNotice);
export type CollectionNoticeListType = z.infer<typeof CollectionNoticeList>;

export type CollectionCorrectionType = z.infer<typeof CollectionCorrection>;
export const CollectionCorrectionList = z.array(CollectionCorrection);
export type CollectionCorrectionListType = z.infer<
  typeof CollectionCorrectionList
>;

export type CollectionDisputeType = z.infer<typeof CollectionDispute>;
export const CollectionDisputeList = z.array(CollectionDispute);
export type CollectionDisputeListType = z.infer<typeof CollectionDisputeList>;

export type InvoiceType = z.infer<typeof Invoice>;
export const InvoiceList = z.array(Invoice);
export type InvoiceListType = z.infer<typeof InvoiceList>;

export type InvoiceItemType = z.infer<typeof InvoiceItem>;
export const InvoiceItemList = z.array(InvoiceItem);
export type InvoiceItemListType = z.infer<typeof InvoiceItemList>;

export type CustomerInfoType = z.infer<typeof CustomerInfo>;
export const CustomerInfoList = z.array(CustomerInfo);
export type CustomerInfoListType = z.infer<typeof CustomerInfoList>;

export type AmountType = z.infer<typeof Amount>;
export const AmountList = z.array(Amount);
export type AmountListType = z.infer<typeof AmountList>;

export type PaymentMethodType = z.infer<typeof PaymentMethod>;
export const PaymentMethodList = z.array(PaymentMethod);
export type PaymentMethodListType = z.infer<typeof PaymentMethodList>;

export type TaxType = z.infer<typeof Tax>;
export const TaxList = z.array(Tax);
export type TaxListType = z.infer<typeof TaxList>;

export type DiscountType = z.infer<typeof Discount>;
export const DiscountList = z.array(Discount);
export type DiscountListType = z.infer<typeof DiscountList>;

// Pagination schemas for violations list
export const ViolationPaginationNextKeySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
});
export type ViolationPaginationNextKey = z.infer<
  typeof ViolationPaginationNextKeySchema
>;

export const ViolationsPaginatedResult = createPaginatedResultSchema(
  Violation,
  ViolationPaginationNextKeySchema
);
export type ViolationsPaginatedResultType = z.infer<
  typeof ViolationsPaginatedResult
>;

const TriageEnrichmentVehicleSchema = z.object({
  plateState: z.string().nullish(),
  vehicleMake: z.string().nullish(),
  vehicleModel: z.string().nullish(),
  /** Plate on the historic violation / permit row used for this hint */
  matchedFromPlate: z.string().nullish(),
  matchedFromPlateState: z.string().nullish(),
  /** `violation` | `permit` */
  matchedFromSource: z.string().nullish(),
  matchedFromViolationId: z.string().nullish(),
  matchedFromPermitId: z.string().nullish(),
  matchedFromPlateEditDistance: z.number().nullish(),
});

const TriageEnrichmentSignalSchema = z.object({
  code: z.string(),
  detail: z.string().nullish(),
});

/** DB-backed triage hints (vehicle + suggested reasons) from parking-app-api */
export const TriageEnrichmentSchema = z.object({
  // Go json omitempty may omit empty structs/slices; accept missing keys like other API schemas.
  vehicle: TriageEnrichmentVehicleSchema.nullish().transform((v) => v ?? {}),
  signals: z
    .array(TriageEnrichmentSignalSchema)
    .nullish()
    .transform((s) => s ?? []),
  suggestedReasonNames: z
    .array(z.string())
    .nullish()
    .transform((s) => s ?? []),
  defaultReasonName: z
    .string()
    .nullish()
    .transform((s) => (s == null ? undefined : s)),
});
export type TriageEnrichmentType = z.infer<typeof TriageEnrichmentSchema>;

// Upload session schemas
export const UploadSessionSchema = z.object({
  token: z.string(),
  qrUrl: z.string().optional(),
  expiresIn: z.number(),
});

export const UploadSessionImageSchema = z.object({
  imageId: z.string(),
  thumbnailUrl: z.string(),
  count: z.number().optional(),
});

export const UploadSessionImagesResponseSchema = z.object({
  images: z.array(UploadSessionImageSchema),
});

export type UploadSessionType = z.infer<typeof UploadSessionSchema>;
export type UploadSessionImageType = z.infer<typeof UploadSessionImageSchema>;
export type UploadSessionImagesResponseType = z.infer<
  typeof UploadSessionImagesResponseSchema
>;

// Create Violation Schema for manual violation creation
export const CreateViolationSchema = z.object({
  plate: z
    .string()
    .min(1, 'Plate is required')
    .regex(/^[A-Za-z0-9]+$/, PLATE_CHARS_MESSAGE),
  plateState: z.enum(
    PLATE_STATE_OPTIONS.map((s) => s.abbreviation) as [string, ...string[]],
    {
      errorMap: () => ({ message: 'Invalid US state abbreviation' }),
    }
  ),
  vehicleMake: z.string().min(1, 'Vehicle make is required'),
  vehicleModel: z.string().min(1, 'Vehicle model is required'),
  vehicleColor: z.string().min(1, 'Vehicle color is required'),
  reason: z.string().min(1, 'Reason is required'),
  notes: z.string().max(500, 'Notes cannot exceed 500 characters').optional(),
  externalNotes: z
    .string()
    .max(500, 'External notes cannot exceed 500 characters')
    .optional(),
  blockFaceId: z.string().uuid().optional(),
  bypassChalking: z.boolean().optional(),
  /** When true, allows creating a second violation for same plate+reason in the same day (support/PEO override). */
  bypassDailyViolationLimit: z.boolean().optional(),
  /** When set (support Allow flow), backend deletes this blocked audit row after successful create. */
  blockedAuditEntryId: z.coerce.number().optional(),
  images: z
    .array(z.instanceof(File))
    .max(2, 'Maximum 2 images allowed')
    .refine(
      (files) => files.every((f) => f.size <= 5 * 1024 * 1024),
      'Each image must be less than 5MB'
    )
    .refine(
      (files) =>
        files.every((f) => ['image/png', 'image/jpeg'].includes(f.type)),
      'Only PNG and JPEG images are allowed'
    )
    .optional(),
  uploadToken: z.string().uuid().optional(),
  // Officer fields (optional, for public/government enforcement locations)
  officerName: z
    .string()
    .max(100, 'Officer name cannot exceed 100 characters')
    .optional(),
  address: z
    .string()
    .max(255, 'Address cannot exceed 255 characters')
    .optional(),
});

export type CreateViolationInput = z.infer<typeof CreateViolationSchema>;

// === Duplicate Violation Check Types ===

/**
 * Location information for duplicate violation display.
 */
export const DuplicateViolationLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type DuplicateViolationLocation = z.infer<
  typeof DuplicateViolationLocationSchema
>;

/**
 * Simplified violation data for duplicate warning display.
 * Status enum matches main Violation schema so duplicates in any state (e.g. paid, voided, waived) parse correctly.
 */
export const DuplicateViolationSchema = z.object({
  id: z.string(),
  plate: z.string(),
  plateState: z.string(),
  reason: z.string(),
  status: ViolationStatusEnum,
  createdAt: zodTimeStamp,
  createdBy: z.string(),
  location: DuplicateViolationLocationSchema,
});

export type DuplicateViolation = z.infer<typeof DuplicateViolationSchema>;

/**
 * Response from the duplicate check endpoint.
 * limitMode is "block" or "warn" when hasDuplicate (location's daily violation limit mode).
 */
export const CheckDuplicateViolationResponseSchema = z.object({
  hasDuplicate: z.boolean(),
  duplicates: z.array(DuplicateViolationSchema),
  limitMode: z.enum(['block', 'warn']).optional(),
});

export type CheckDuplicateViolationResponse = z.infer<
  typeof CheckDuplicateViolationResponseSchema
>;

/**
 * Request parameters for duplicate check.
 */
export type CheckDuplicateViolationParams = {
  locationId: string;
  plate: string;
  plateState: string;
  reason: string;
};

/** Source of blocked attempt: PEO issue or camera create */
export const BlockedIssuanceSourceSchema = z.enum([
  'peo_issue',
  'camera_create',
]);
export type BlockedIssuanceSource = z.infer<typeof BlockedIssuanceSourceSchema>;

/** Safe date parse for API responses: accepts string or null, never throws. Returns null for missing/invalid so UI can show "—". */
const safeCreatedAtSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((val): DateTime | null => {
    if (val == null || val === '') return null;
    const dt = parseISO8601DateTime(val);
    return dt.isValid ? dt : null;
  });

/**
 * Blocked issuance audit entry (same plate + reason + day was blocked).
 * API returns snake_case.
 */
export const BlockedIssuanceAuditStatusSchema = z.enum(['blocked', 'allowed']);

export const BlockedIssuanceAuditEntrySchema = z.object({
  id: z.coerce.number(),
  location_id: z.string(),
  plate: z.string(),
  plate_state: z.string(),
  reason: z.string(),
  submitted_by: z.string().nullish(),
  existing_violation_id: z.string(),
  source: BlockedIssuanceSourceSchema,
  status: BlockedIssuanceAuditStatusSchema.default('blocked'),
  allowed_by: z.string().nullish(),
  allowed_at: safeCreatedAtSchema,
  vehicle_make: z.string().nullish(),
  vehicle_model: z.string().nullish(),
  vehicle_color: z.string().nullish(),
  notes: z.string().nullish(),
  external_notes: z.string().nullish(),
  officer_name: z.string().nullish(),
  officer_badge_number: z.string().nullish(),
  address: z.string().nullish(),
  block_face_id: z.string().nullish(),
  image_paths: z.array(z.string()).nullish(),
  created_at: safeCreatedAtSchema,
});
export type BlockedIssuanceAuditEntry = z.infer<
  typeof BlockedIssuanceAuditEntrySchema
>;

export const BlockedAuditPaginationStartKeySchema = z.object({
  id: z.coerce.number(),
  createdAt: z.string(),
});
export type BlockedAuditPaginationStartKey = z.infer<
  typeof BlockedAuditPaginationStartKeySchema
>;

export const BlockedIssuanceAuditListResultSchema = createPaginatedResultSchema(
  BlockedIssuanceAuditEntrySchema,
  BlockedAuditPaginationStartKeySchema
);
export type BlockedIssuanceAuditListResult = z.infer<
  typeof BlockedIssuanceAuditListResultSchema
>;

export type ListBlockedIssuanceAuditParams = {
  locationId?: string;
  plate?: string;
  plateState?: string;
  source?: BlockedIssuanceSource;
  createdAfter?: string;
  createdBefore?: string;
  limit?: number;
  startKey?: BlockedAuditPaginationStartKey;
};

/**
 * Frequent offender: plate with multiple blocked attempts (for review).
 * API returns snake_case. Sorted by attempt_count DESC (most blocked first).
 */
export const FrequentOffenderSummarySchema = z.object({
  plate: z.string(),
  plate_state: z.string(),
  reason: z.string(),
  attempt_count: z.coerce.number(),
  last_attempt_at: safeCreatedAtSchema,
});
export type FrequentOffenderSummary = z.infer<
  typeof FrequentOffenderSummarySchema
>;

export const FrequentOffenderPaginationStartKeySchema = z.object({
  attemptCount: z.coerce.number(),
  plate: z.string(),
  reason: z.string(),
});
export type FrequentOffenderPaginationStartKey = z.infer<
  typeof FrequentOffenderPaginationStartKeySchema
>;

export const FrequentOffendersListResultSchema = createPaginatedResultSchema(
  FrequentOffenderSummarySchema,
  FrequentOffenderPaginationStartKeySchema
);
export type FrequentOffendersListResult = z.infer<
  typeof FrequentOffendersListResultSchema
>;

export type ListFrequentOffendersParams = {
  locationId?: string;
  createdAfter?: string;
  createdBefore?: string;
  minAttempts?: number;
  limit?: number;
  startKey?: FrequentOffenderPaginationStartKey;
};

// === Disposition Reason Catalog ===

/** Categories used to group disposition reasons in dashboards. */
export const DispositionCategorySchema = z.enum([
  'valid_entitlement',
  'payment_state',
  'discretionary',
  'enforcement_policy',
  'system_error',
  'data_quality',
  'operational',
  'duplicate',
  'test',
  'other',
]);
export type DispositionCategory = z.infer<typeof DispositionCategorySchema>;

/** Violation status values for which a disposition reason can apply. */
const KNOWN_DISPOSITION_APPLIES_TO = ['voided', 'waived'] as const;
export const DispositionAppliesToSchema = z.enum(KNOWN_DISPOSITION_APPLIES_TO);
export type DispositionAppliesTo = z.infer<typeof DispositionAppliesToSchema>;

const isKnownDispositionAppliesTo = (
  value: string
): value is DispositionAppliesTo =>
  (KNOWN_DISPOSITION_APPLIES_TO as readonly string[]).includes(value);

export const DispositionReasonDefSchema = z.object({
  value: z.string(),
  label: z.string(),
  // Forward-compat: unknown categories fall back to 'other' so a new
  // server-side category cannot wedge the picker by failing the parse.
  category: DispositionCategorySchema.catch('other'),
  // Forward-compat: unknown appliesTo values are dropped rather than
  // failing the parse; the affected reason simply won't appear in any
  // picker until the client learns the new value.
  appliesTo: z
    .array(z.string())
    .transform((arr) => arr.filter(isKnownDispositionAppliesTo)),
});
export type DispositionReasonDef = z.infer<typeof DispositionReasonDefSchema>;

export const DispositionReasonCatalogResponseSchema = z.object({
  reasons: z.array(DispositionReasonDefSchema),
});
export type DispositionReasonCatalogResponse = z.infer<
  typeof DispositionReasonCatalogResponseSchema
>;

/** Sentinel slug agents pick to write a free-text detail instead of a catalog slug. */
export const DISPOSITION_REASON_OTHER = 'other';

export const EvidencePackageResultSchema = z.object({
  url: z.string().url(),
  expiresAt: zodTimeStamp,
  sizeBytes: z.number().int().nonnegative(),
});
export type EvidencePackageResult = z.infer<typeof EvidencePackageResultSchema>;
